import { useState, useEffect, Fragment, useCallback, useRef } from 'react';

import {
  Autocomplete,
  Box,
  Drawer,
  Grid,
  TextField,
  Button,
  Divider,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import PlaceIcon from '@mui/icons-material/Place';

import messages from '../../static/messages';
import placesServices from '../../services/placesServices';
import propertiesServices from '../../services/propertiesServices';
import { button } from '../../static/theme-styles';

const required_fields = ['streetNumber', 'streetName', 'city', 'state', 'zipCode'];

const PROPERTY_TYPES = [
  { name: 'Apartments' },
  { name: 'Single home' },
  { name: 'Town house' },
  { name: 'Condo' },
  { name: 'Mobile home' },
  { name: 'Building' }
];

const PropertyDrawer = (props) => {
  const { currentProperty, setCurrentProperty, createProperty, updateProperty } = props;
  const [errors, setErrors] = useState({});
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  const [addressSelected, setAddressSelected] = useState(false);
  const sessionTokenRef = useRef(placesServices.generateSessionToken());
  const debounceRef = useRef(null);

  const searchAddresses = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setAddressOptions([]);
      return;
    }

    setAddressLoading(true);
    try {
      const [dbResponse, googleResponse] = await Promise.allSettled([
        propertiesServices.searchRequest(0, 10, query),
        placesServices.autocomplete(query, sessionTokenRef.current),
      ]);

      const options = [];

      if (dbResponse.status === 'fulfilled' && dbResponse.value?.data?.data) {
        dbResponse.value.data.data.forEach((prop) => {
          const addr = [
            prop.streetNumber, prop.streetName,
            prop.unit ? `#${prop.unit}` : '',
          ].filter(Boolean).join(' ');
          const full = [addr, prop.city, `${prop.state || ''} ${prop.zipCode || ''}`].filter(Boolean).join(', ');
          options.push({
            type: 'db',
            id: prop.id,
            label: full.trim(),
            property: prop,
          });
        });
      }

      if (googleResponse.status === 'fulfilled' && googleResponse.value?.data) {
        const predictions = Array.isArray(googleResponse.value.data) ? googleResponse.value.data : [];
        predictions.forEach((pred) => {
          options.push({
            type: 'google',
            placeId: pred.placeId,
            label: pred.description,
            mainText: pred.mainText,
            secondaryText: pred.secondaryText,
          });
        });
      }

      setAddressOptions(options);
    } catch (err) {
      console.log('Address search error:', err);
    } finally {
      setAddressLoading(false);
    }
  }, []);

  const handleAddressInputChange = (event, value, reason) => {
    if (reason === 'input') {
      setAddressInput(value);
      setAddressSelected(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => searchAddresses(value), 350);
    }
    if (reason === 'clear') {
      setAddressInput('');
      setAddressOptions([]);
      setAddressSelected(false);
    }
  };

  const handleAddressSelect = async (event, option) => {
    if (!option || typeof option === 'string') return;

    if (option.type === 'db') {
      const p = option.property;
      setCurrentProperty((prev) => ({
        ...prev,
        streetNumber: p.streetNumber || '',
        streetName: p.streetName || '',
        city: p.city || '',
        state: p.state || '',
        zipCode: p.zipCode || '',
        unit: p.unit || '',
        isEdited: true,
      }));
      setAddressSelected(true);
    } else if (option.type === 'google') {
      try {
        const response = await placesServices.getDetails(option.placeId, sessionTokenRef.current);
        if (response?.data) {
          const d = response.data;
          setCurrentProperty((prev) => ({
            ...prev,
            streetNumber: d.streetNumber || '',
            streetName: d.streetName || '',
            city: d.city || '',
            state: d.state || '',
            zipCode: d.zipCode || '',
            unit: '',
            isEdited: true,
          }));
          setAddressSelected(true);
          sessionTokenRef.current = placesServices.generateSessionToken();
        }
      } catch (err) {
        console.log('Error getting place details:', err);
      }
    }

    setAddressOptions([]);
  };

  const onPropertyTypeSelected = (e, val) => {
    e.preventDefault();
    setCurrentProperty((prevState) => ({
      ...prevState,
      typeName: val?.name || val,
      isEdited: true,
    }));
  };

  const resetErrors = () => {
    required_fields.forEach((el) =>
      setErrors((prevState) => ({
        ...prevState,
        [el]: false,
      })),
    );
  };

  const handleValidate = () => {
    required_fields.forEach((field) => {
      const el = currentProperty[field];
      if (el === '' || el === null || el === undefined) {
        setErrors((prevState) => ({
          ...prevState,
          [field]: true,
        }));
      } else {
        setErrors((prevState) => ({
          ...prevState,
          [field]: false,
        }));
      }
    });
  };

  const handleClose = () => props.onCloseDrawer(resetErrors);

  useEffect(() => {
    resetErrors();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (currentProperty?.isEdited) {
      !Object.values(errors).includes(true) && (props.isNewProperty ? createProperty() : updateProperty());
    }
  }, [errors]);

  return (
    <Drawer anchor="right" open={props.drawer} onClose={handleClose}>
      <Box>
        {currentProperty && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box>
                <Typography variant="h4" gutterBottom>
                  {props.isNewProperty ? 'Add New Property' : 'Update Property'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                value={currentProperty?.type || (currentProperty?.typeName ? { name: currentProperty.typeName } : null)}
                options={PROPERTY_TYPES}
                getOptionLabel={(option) => option.name || option || ''}
                onChange={onPropertyTypeSelected}
                isOptionEqualToValue={(option, value) => option.name === (value?.name || value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Property Type"
                    required
                  />
                )}
              />
            </Grid>

            {props.isNewProperty && (
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={addressOptions}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
                  filterOptions={(x) => x}
                  inputValue={addressInput}
                  onInputChange={handleAddressInputChange}
                  onChange={handleAddressSelect}
                  loading={addressLoading}
                  groupBy={(option) => option.type === 'db' ? 'Existing Properties' : 'Google Suggestions'}
                  renderOption={(optionProps, option) => (
                    <li {...optionProps} key={option.placeId || option.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {option.type === 'db' ? (
                          <HomeIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                        ) : (
                          <PlaceIcon sx={{ color: '#f57c00', fontSize: 20 }} />
                        )}
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {option.label}
                        </Typography>
                        {option.type === 'db' && (
                          <Chip label="DB" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Address"
                      placeholder="Start typing address..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 0.5 }} />,
                        endAdornment: (
                          <>
                            {addressLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      helperText={addressSelected
                        ? "Address filled from suggestion. Review and edit if needed."
                        : "Search by street name, number, or full address"
                      }
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={6}>
              <TextField
                required
                label="Street Number"
                name="streetNumber"
                value={currentProperty.streetNumber || ''}
                fullWidth
                error={errors.streetNumber}
                helperText={errors.streetNumber && messages.errors.required_field}
                onChange={(event) =>
                  setCurrentProperty((prevState) => ({
                    ...prevState,
                    streetNumber: event.target.value.replace(/\s/g, ''),
                    isEdited: true,
                  }))
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                required
                label="Street Name"
                name="streetName"
                value={currentProperty.streetName || ''}
                fullWidth
                error={errors.streetName}
                helperText={errors.streetName && messages.errors.required_field}
                onChange={(event) =>
                  setCurrentProperty((prevState) => ({
                    ...prevState,
                    streetName: event.target.value,
                    isEdited: true,
                  }))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Unit / Apt #"
                name="unit"
                value={currentProperty.unit || ''}
                fullWidth
                onChange={(event) =>
                  setCurrentProperty((prevState) => ({
                    ...prevState,
                    unit: event.target.value,
                    isEdited: true,
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                label="City"
                name="city"
                value={currentProperty.city || ''}
                fullWidth
                error={errors.city}
                helperText={errors.city && messages.errors.required_field}
                onChange={(event) =>
                  setCurrentProperty((prevState) => ({
                    ...prevState,
                    city: event.target.value,
                    isEdited: true,
                  }))
                }
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                required
                label="State"
                name="state"
                value={currentProperty.state || ''}
                fullWidth
                inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                error={errors.state}
                helperText={errors.state && messages.errors.required_field}
                onChange={(event) =>
                  setCurrentProperty((prevState) => ({
                    ...prevState,
                    state: event.target.value.toUpperCase(),
                    isEdited: true,
                  }))
                }
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                required
                label="ZIP Code"
                name="zipCode"
                value={currentProperty.zipCode || ''}
                fullWidth
                inputProps={{ maxLength: 10 }}
                error={errors.zipCode}
                helperText={errors.zipCode && messages.errors.required_field}
                onChange={(event) =>
                  setCurrentProperty((prevState) => ({
                    ...prevState,
                    zipCode: event.target.value,
                    isEdited: true,
                  }))
                }
              />
            </Grid>

            {props.showInvalid && (
              <Fragment>
                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(event) =>
                          setCurrentProperty((prevState) => ({
                            ...prevState,
                            isCorrect: event.target.checked,
                            isEdited: true,
                          }))
                        }
                      />
                    }
                    label="Is Correct"
                  />
                </Grid>
              </Fragment>
            )}

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                sx={{ ...button('primary', 'secondary'), mr: 3 }}
                onClick={handleValidate}
                disabled={props.showInvalid ? !currentProperty.isCorrect : !currentProperty.isEdited}
              >
                {props.isNewProperty ? 'Add New Property' : 'Update Property'}
              </Button>

              <Button variant="contained" sx={button('secondary')} onClick={handleClose}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
    </Drawer>
  );
};

export default PropertyDrawer;
